import React, { useState, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { DatePicker } from 'components/forms/DatePicker'
import { ISelectOption } from 'components/forms/Selects'
import moment from 'moment'
import { CurrencyInput } from 'transfix-ui/components/CurrencyInput'
import { FormModal } from 'transfix-ui/components/Modals'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { ILoadBoardPost, ILoadBoardPostState } from 'types/loadBoardPosts'
import { IShipment } from 'types/shipment'
import { updateLoadBoardPost, closeLoadBoardPostModal } from 'utils/actions/loadBoardPosts'
import uuid from 'uuid'
import { ValidationError } from 'yup'
import { DAT_EQUIPMENT_TYPES } from 'apps/booking/constants/loadBoardPosts'
import {
  convertLbpApiToState,
  convertLbpApiArrToPostsState,
  mergeCommonPostAttrs,
} from 'apps/booking/utils/loadBoardPost'
import { IStore } from 'apps/booking/utils/reducers'
import {
  commonPostSchema,
  activePostsObjectSchema,
  activePostsSchema,
} from 'apps/booking/utils/validationSchema'
import { compileErrors } from 'apps/booking/utils/yupValidation'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { FORMATS } from 'constants/datetime'
import { ActivePostField } from './ActivePostField'

export enum STOP_TYPE {
  origin = 'origin',
  destination = 'destination',
}

interface ISelector {
  shipment?: IShipment
  showModal: boolean
  editing: boolean
  editingError?: string
}

export interface IValidationErrorStates {
  [key: string]: string
}

export const EditPostDialog = memo(({ posts }: { posts: ILoadBoardPost[] }) => {
  const { trackEvent } = useMPAnalytics()
  const { editing, editingError, shipment, showModal } = useSelector<IStore, ISelector>(
    ({ loadBoardPosts, sidebar: { data } }) => ({
      shipment: data?.shipment,
      showModal: loadBoardPosts.showModal,
      editing: loadBoardPosts.editing,
      editingError: loadBoardPosts.editingError,
    })
  )
  const dispatch = useDispatch()
  const closeModal = () => {
    dispatch(closeLoadBoardPostModal())
  }

  const defaultState = {
    origin: {
      city: '',
      state: '',
    },
    destination: {
      city: '',
      state: '',
    },
    rate: '0',
    pickupDate: moment().format(FORMATS.dateFormat),
    equipmentType: DAT_EQUIPMENT_TYPES.VAN,
    commentsOne: '',
    commentsTwo: '',
    includeListRate: true,
  }

  const [commonPost, setCommonPost] = useState(
    posts.length > 0 ? convertLbpApiToState(posts[0]) : defaultState
  )
  const [newPost, setNewPost] = useState<ILoadBoardPostState>(defaultState)
  const [activePosts, setActivePosts] = useState(convertLbpApiArrToPostsState(posts))
  const [validationErrors, setValidationErrors] = useState<IValidationErrorStates>({})

  // Overall Post
  const setListRate = (e: { value: string }) => {
    setCommonPost({ ...commonPost, rate: e.value })
  }
  const setCommentsTwo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommonPost({ ...commonPost, commentsTwo: e.target.value })
  }
  const setStartDate = (e: string[]) => {
    setCommonPost({
      ...commonPost,
      pickupDate: e[0] ? moment(e[0]).format(FORMATS.dateFormat) : '',
    })
  }

  // Adding Posts
  const addOrigin = ({ city, state }: ILocationSuggestion) => {
    setNewPost({
      ...newPost,
      origin: {
        city: city ?? '',
        state: state ?? '',
      },
    })
  }

  const addDestination = ({ city, state }: ILocationSuggestion) => {
    setNewPost({
      ...newPost,
      destination: {
        city: city ?? '',
        state: state ?? '',
      },
    })
  }

  const addIncludeListRate = () => {
    setNewPost({ ...newPost, includeListRate: !newPost.includeListRate })
  }
  const addEquipmentType = ({ value }: ISelectOption<DAT_EQUIPMENT_TYPES>) => {
    setNewPost({
      ...newPost,
      equipmentType: value,
    })
  }

  const filteredActivePosts = activePosts.filter(post => !post._destroy)

  const addActivePost = () => {
    trackEvent('Booking', 'CLICKED_ADD_POST', {
      shipment: shipment?.id,
      post: newPost,
    })
    if (activePostsSchema.isValidSync(filteredActivePosts)) {
      setActivePosts([...activePosts, { ...newPost, uuid: uuid() }])
      setNewPost(defaultState)
    }
  }

  // Active Posts
  const updateActivePost = (
    updatedPostAttribute = {},
    uuidStr: string,
    includeListRate = false
  ) => {
    setActivePosts(
      activePosts.map(activePost => {
        if (activePost.uuid === uuidStr) {
          updatedPostAttribute = includeListRate
            ? { includeListRate: !activePost.includeListRate }
            : updatedPostAttribute
          return {
            ...activePost,
            ...updatedPostAttribute,
          }
        }
        return activePost
      })
    )
  }

  const setOrigin = (location: ILocationSuggestion, uuidStr: string) => {
    updateActivePost({ origin: location }, uuidStr)
  }
  const setDestination = (location: ILocationSuggestion, uuidStr: string) => {
    updateActivePost({ destination: location }, uuidStr)
  }
  const setEquipmentType = ({ value }: ISelectOption<DAT_EQUIPMENT_TYPES>, uuidStr: string) => {
    updateActivePost({ equipmentType: value }, uuidStr)
  }
  const setIncludeListRate = (uuidStr: string) => {
    updateActivePost({}, uuidStr, true)
  }
  const deleteActivePost = (uuidStr: string) => {
    trackEvent('Booking', 'CLICKED_DELETE_POST', {
      shipment: shipment?.id,
    })
    updateActivePost({ _destroy: true }, uuidStr)
  }

  const saveEditedPosts = () => {
    const validatePostStops = activePostsObjectSchema
      .validate(filteredActivePosts, { abortEarly: false })
      .catch(e => e)
    const validateCommonAttrs = commonPostSchema
      .validate(commonPost, { abortEarly: false })
      .catch(e => e)

    Promise.all([validatePostStops, validateCommonAttrs]).then(results => {
      const containsErrors = results.filter(result => result instanceof ValidationError)
      if (containsErrors.length > 0) {
        const allErrors = containsErrors.reduce(
          (acc, err) => ({ ...acc, ...compileErrors(err) }),
          {}
        )
        setValidationErrors(allErrors)
      } else {
        setValidationErrors({})
        const updatedPosts = mergeCommonPostAttrs(activePosts, commonPost).filter(
          relevantPosts => !(relevantPosts._destroy && !relevantPosts.id)
        )
        trackEvent('Booking', 'CLICKED_EDIT_POST', {
          loadBoardPosts: updatedPosts,
        })
        if (shipment?.id) {
          dispatch(updateLoadBoardPost(shipment?.id.toString(), updatedPosts))
        }
      }
    })
  }

  return (
    <FormModal
      title='Edit DAT Posts'
      size={7}
      open={showModal}
      onClose={closeModal}
      error={editingError}
      dataTest='edit-dat-post'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'cancel-dat-posts',
        },
        {
          label: 'Save',
          type: 'confirm',
          action: saveEditedPosts,
          disabled: editing,
          dataTest: 'save-dat-posts',
        },
      ]}>
      <>
        <div className='row flex-l items-center mb2'>
          <div className='col-xs-2'>
            <CurrencyInput
              placeholder='$'
              label='List Rate'
              id='rate'
              value={Number(commonPost.rate) || 0}
              onChange={setListRate}
              dataTest='rate'
              invalid={!!validationErrors.rate}
              validationMessage={validationErrors.rate}
            />
          </div>
        </div>
        <div className='col-xs-12 text-caption-semibold mb2'>Rate on the Transfix app and DAT.</div>
        <div className='row mb2'>
          <div className='col-xs-3'>
            <label className='label' htmlFor='pickup-date'>
              Pickup Date
            </label>
            <DatePicker
              date={commonPost.pickupDate}
              placeholder='Pickup Date'
              onDateChange={setStartDate}
              dataTest='pickup-date'
              id='pickup-date'
            />
          </div>
        </div>
        <div className='row mb2'>
          <div className='col-xs-8'>
            <label className='label' htmlFor='comments-one'>
              Comments 1
            </label>
            <textarea
              name='comments-one'
              data-test='comments-one'
              value={commonPost.commentsOne}
              disabled
            />
          </div>
        </div>
        <div className='row mb2'>
          <div className='col-xs-8'>
            <label className='label' htmlFor='comments-two'>
              Comments 2
            </label>
            <textarea
              name='comments-two'
              data-test='comments-two'
              value={commonPost.commentsTwo}
              className={validationErrors.commentsTwo ? 'invalid' : ''}
              onChange={setCommentsTwo}
            />
            {!!validationErrors.commentsTwo && (
              <span className='validation-message text-error'>{validationErrors.commentsTwo}</span>
            )}
          </div>
        </div>
        <div className='row mb2'>
          <div className='col-xs-12 body-medium'>Active Post</div>
          <div className='col-xs-12'>Posts are automatically refreshed every 15 minutes.</div>
        </div>
        <ActivePostField
          activePost={newPost}
          setOrigin={addOrigin}
          setDestination={addDestination}
          setEquipment={addEquipmentType}
          setListRate={addIncludeListRate}
          buttonAction={addActivePost}
          showAdd
        />
        <div className='row mb2'>
          <hr className='bg-disabled mh0' />
        </div>
        {filteredActivePosts.map((activePost, idx) => {
          const uuidString = activePost.uuid
          const setActiveOrigin = (e: ILocationSuggestion) => {
            setOrigin(e, uuidString)
          }
          const setActiveDestination = (e: ILocationSuggestion) => {
            setDestination(e, uuidString)
          }
          const setActiveEquipmentType = (e: ISelectOption<DAT_EQUIPMENT_TYPES>) => {
            setEquipmentType(e, uuidString)
          }
          const setActiveIncludeListRate = () => {
            setIncludeListRate(uuidString)
          }
          const setActiveDeletePost = () => {
            deleteActivePost(uuidString)
          }
          const errorForPost = () => {
            if (validationErrors && Object.keys(validationErrors).length) {
              const stopErrors = Object.keys(validationErrors).filter(
                key => key.split('.')[0] === `[${idx}]`
              )
              return {
                originError: stopErrors.find(key => key.split('.')[1] === STOP_TYPE.origin)
                  ? 'Origin required'
                  : '',
                destinationError: stopErrors.find(
                  key => key.split('.')[1] === STOP_TYPE.destination
                )
                  ? 'Destination required'
                  : '',
              }
            }
            return undefined
          }
          return (
            <ActivePostField
              activePost={activePost}
              setOrigin={setActiveOrigin}
              setDestination={setActiveDestination}
              setEquipment={setActiveEquipmentType}
              setListRate={setActiveIncludeListRate}
              buttonAction={setActiveDeletePost}
              index={idx}
              showDelete={filteredActivePosts.length !== 1}
              validationErrors={errorForPost()}
              key={uuidString}
            />
          )
        })}
      </>
    </FormModal>
  )
})
